// src/app/views/prueba-testimonial/dialog-form-prueba-testimonial/dialog-form-prueba-testimonial.component.ts
import { ChangeDetectionStrategy, Component, Inject, Optional, OnInit, ChangeDetectorRef, Input } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatOptionModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';

import { AccionesDialog } from '@models/base/identificadores.model';
import { ExpedienteService } from '@services/expediente/expediente.service';

import { ButtonCancelDialogComponent } from '@component/button-cancel-dialog/button-cancel-dialog.component';
import { ButtonCloseDialogComponent } from '@component/button-close-dialog/button-close-dialog.component';
import { ButtonAddComponent } from '@component/button-add/button-add.component';
import { ButtonSaveComponent } from '@component/button-save/button-save.component';
import { ButtonDeleteComponent } from '@component/button-delete/button-delete.component';
import { PersonaDetailDto } from '@models/persona/personaDetailDto.model';
import { CreatePruebaTestimonialRequest } from '@models/pruebas/pruebaTestimonial/createPruebaTestimonialRequest.model';
import { PruebaTestimonialDto } from '@models/pruebas/pruebaTestimonial/pruebaTestimonialDto.model';
import { UpdatePruebaTestimonialRequest } from '@models/pruebas/pruebaTestimonial/updatePruebaTestimonialRequest.model';
import { EstadoPruebaService } from '@services/estadoPrueba/estado-prueba.service';
import { PruebaTestimonialService } from '@services/pruebas/pruebaTestimonial/pruebaTestimonial.service';
import { SnackBarService } from '@services/snackBar/snack-bar-service.service';
import { TipoIntervinienteService } from '@services/tipoInterviniente/tipoInterviniente.service';
import { UsuarioService } from '@services/usuario/usuario.service';
import { BuscarPersonaComponent } from '@views/busquedas/buscar-persona/buscar-persona.component';
import { MatAutocompleteModule } from '@angular/material/autocomplete';

@Component({
  selector: 'app-dialog-form-prueba-testimonial',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatDividerModule,
    NgxSpinnerModule,
    MatTableModule,
    MatSliderModule,
    MatSlideToggleModule,
    MatMenuModule,
    MatDialogModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatExpansionModule,
    MatSelectModule,
    MatOptionModule,
    MatTabsModule, // Agregado para tabs
    ButtonCancelDialogComponent,
    ButtonCloseDialogComponent,
    ButtonAddComponent,
    ButtonSaveComponent,
    ButtonDeleteComponent,
    MatAutocompleteModule,
    BuscarPersonaComponent,
    BuscarPersonaComponent
  ],
  templateUrl: './dialog-form-prueba-testimonial.component.html',
  styleUrl: './dialog-form-prueba-testimonial.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DialogFormPruebaTestimonialComponent implements OnInit {
  @Input() idExpediente: any;
  formBuilder = new FormBuilder();
  pruebaTestimonialDto!: PruebaTestimonialDto;
  usuarios: any[] = [];
  tiposIntervinientes: any[] = [];
  estadosPrueba: any[] = [];
  expedientes: any[] = [];

  // Persona testigo
  personaTestigoSeleccionada: PersonaDetailDto | null = null;

  // Autocomplete para medio registro
  medioRegistroControl = new FormControl('');
  mediosRegistroSugeridos: string[] = [
    'Audio',
    'Video',
    'Escrito',
    'Digital',
    'Presencial'
  ];
  filteredMediosRegistro!: Observable<string[]>;
  expedienteSeleccionado: any;

  // Getter para acceder a los controles del formulario fácilmente
  get f() { return this.form.controls; }

  accionesDialog = AccionesDialog;
  form!: FormGroup;
  localData: any;

  // Para el template - asegurar que PersonaDetailDto esté disponible
  PersonaDetailDto = PersonaDetailDto;

  constructor(
    public dialogRef: MatDialogRef<DialogFormPruebaTestimonialComponent>,
    private snackBar: SnackBarService,
    private spinner: NgxSpinnerService,
    private pruebaTestimonialService: PruebaTestimonialService,
    private usuarioService: UsuarioService,
    private tipoIntervinienteService: TipoIntervinienteService,
    private estadoPruebaService: EstadoPruebaService,
    private expedienteService: ExpedienteService,
    private cdr: ChangeDetectorRef, // Para OnPush
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.localData = { ...data };
    this.initializeForm();
    this.setupMedioRegistroAutocomplete();
  }

  ngOnInit(): void {
    this.loadCatalogs();
    this.setFormData();
  }

  initializeForm() {
    this.form = this.formBuilder.group({
      Descripcion: ["", [Validators.required, Validators.maxLength(500)]],
      Observaciones: ["", [Validators.maxLength(1000)]],
      IdUsuarioResponsable: [""],
      IdTipoInterviniente: [""],
      IdEstadoPrueba: [""],
      FechaDeclaraion: [""],
      AutoridadDeclaracion: ["", [Validators.maxLength(200)]],
      Declaracion: ["", [Validators.maxLength(2000)]],
      Grupo: ["", [Validators.min(1)]],
      FechaPrimeraAudiencia: [""],
      FechaCedulaPresentacionPrimeraAudiencia: [""],
      FechaCedulaNotificacionPrimeraAudiencia: [""],
      FechaSegundaAudiencia: [""],
      FechaCedulaPresentacionSegundaAudiencia: [""],
      FechaCedulaNotificacionSegundaAudiencia: [""],
      IdExpediente: [""],
      MedioRegistro: this.medioRegistroControl
    });
  }

  private setupMedioRegistroAutocomplete(): void {
    this.filteredMediosRegistro = this.medioRegistroControl.valueChanges.pipe(
      startWith(''),
      map(value => this._filterMediosRegistro(value || ''))
    );
  }

  private _filterMediosRegistro(value: string): string[] {
    const filterValue = value.toLowerCase();
    return this.mediosRegistroSugeridos.filter(option =>
      option.toLowerCase().includes(filterValue)
    );
  }

  setFormData() {
    if (this.data?.accion === AccionesDialog.Modificar && this.data?.id) {
      this.form.patchValue({
        Descripcion: this.data.descripcion || '',
        Observaciones: this.data.observaciones || '',
        IdUsuarioResponsable: this.data.usuarioResponsable?.id || '',
        IdTipoInterviniente: this.data.tipoInterviniente?.id || '',
        IdEstadoPrueba: this.data.estadoPrueba?.id || '',
        FechaDeclaraion: this.data.fechaDeclaraion || null,
        AutoridadDeclaracion: this.data.autoridadDeclaracion || '',
        Declaracion: this.data.declaracion || '',
        Grupo: this.data.grupo || '',
        FechaPrimeraAudiencia: this.data.fechaPrimeraAudiencia || null,
        FechaCedulaPresentacionPrimeraAudiencia: this.data.fechaCedulaPresentacionPrimeraAudiencia || null,
        FechaCedulaNotificacionPrimeraAudiencia: this.data.fechaCedulaNotificacionPrimeraAudiencia || null,
        FechaSegundaAudiencia: this.data.fechaSegundaAudiencia || null,
        FechaCedulaPresentacionSegundaAudiencia: this.data.fechaCedulaPresentacionSegundaAudiencia || null,
        FechaCedulaNotificacionSegundaAudiencia: this.data.fechaCedulaNotificacionSegundaAudiencia || null,
        IdExpediente: this.data.idExpediente || this.idExpediente || ''
      });

      this.medioRegistroControl.setValue(this.data.medioRegistro || '');

      // Cargar persona testigo si existe
      if (this.data.personaTestigo) {
        this.personaTestigoSeleccionada = new PersonaDetailDto();
        this.personaTestigoSeleccionada.id = this.data.personaTestigo.id;
        this.personaTestigoSeleccionada.nombreCompleto = this.data.personaTestigo.nombreCompleto;
      }
    }

    if (this.data?.accion === AccionesDialog.Agregar && this.data?.idExpediente) {
      this.form.patchValue({
        IdExpediente: this.data.idExpediente || this.idExpediente || ''
      });
    }

    // Marcar para detección de cambios
    this.cdr.markForCheck();
  }

  loadCatalogs() {
    this.spinner.show('spBusquedaUsuarios');

    // Cargar usuarios
    this.usuarioService.getAll().subscribe({
      next: (usuarios) => {
        this.usuarios = usuarios;
        this.spinner.hide('spBusquedaUsuarios');
        this.cdr.markForCheck();
      },
      error: (error) => {
        this.spinner.hide('spBusquedaUsuarios');
        this.snackBar.showError(error, 'Error al cargar usuarios');
        this.cdr.markForCheck();
      }
    });

    this.spinner.show('spBusquedaTiposIntervinientes');

    // Cargar tipos de interviniente
    this.tipoIntervinienteService.getAll().subscribe({
      next: (tipos) => {
        this.tiposIntervinientes = tipos;
        this.spinner.hide('spBusquedaTiposIntervinientes');
        this.cdr.markForCheck();
      },
      error: (error) => {
        this.spinner.hide('spBusquedaTiposIntervinientes');
        this.snackBar.showError(error, 'Error al cargar tipos de interviniente');
        this.cdr.markForCheck();
      }
    });

    this.spinner.show('spBusquedaEstados');

    // Cargar estados de prueba
    this.estadoPruebaService.getAll().subscribe({
      next: (estados) => {
        this.estadosPrueba = estados;
        this.spinner.hide('spBusquedaEstados');
        this.cdr.markForCheck();
      },
      error: (error) => {
        this.spinner.hide('spBusquedaEstados');
        this.snackBar.showError(error, 'Error al cargar estados de prueba');
        this.cdr.markForCheck();
      }
    });
  }

  onPersonaTestigoSelected(persona: PersonaDetailDto): void {
    this.personaTestigoSeleccionada = persona;
    this.cdr.markForCheck();
  }

  agregar() {
    if (!this.form.valid) {
      this.markFormGroupTouched();
      return;
    }

    const createRequest: CreatePruebaTestimonialRequest = new CreatePruebaTestimonialRequest();
    this.mapFormToCreateRequest(createRequest);
    this.create(createRequest);
  }

  modificar() {
    if (!this.form.valid) {
      this.markFormGroupTouched();
      return;
    }

    const updateRequest: UpdatePruebaTestimonialRequest = new UpdatePruebaTestimonialRequest();
    updateRequest.id = this.data.id;
    this.mapFormToUpdateRequest(updateRequest);
    this.update(updateRequest);
  }

  eliminar() {
    if (!this.data?.id) {
      this.snackBar.showError("No se puede eliminar: ID no válido", "Error");
      return;
    }

    this.delete(this.data.id);
  }

  private mapFormToCreateRequest(request: CreatePruebaTestimonialRequest) {
    request.descripcion = this.form.get("Descripcion")?.value || '';
    request.observaciones = this.form.get("Observaciones")?.value || '';
    request.idUsuarioResponsable = this.form.get("IdUsuarioResponsable")?.value || null;
    request.idPersonaTestigo = this.personaTestigoSeleccionada?.id || null;
    request.idTipoInterviniente = this.form.get("IdTipoInterviniente")?.value || null;
    request.idEstadoPrueba = this.form.get("IdEstadoPrueba")?.value || null;
    request.fechaDeclaraion = this.form.get("FechaDeclaraion")?.value || null;
    request.autoridadDeclaracion = this.form.get("AutoridadDeclaracion")?.value || '';
    request.declaracion = this.form.get("Declaracion")?.value || '';
    request.medioRegistro = this.medioRegistroControl.value || '';
    request.grupo = this.form.get("Grupo")?.value || null;
    request.fechaPrimeraAudiencia = this.form.get("FechaPrimeraAudiencia")?.value || null;
    request.fechaCedulaPresentacionPrimeraAudiencia = this.form.get("FechaCedulaPresentacionPrimeraAudiencia")?.value || null;
    request.fechaCedulaNotificacionPrimeraAudiencia = this.form.get("FechaCedulaNotificacionPrimeraAudiencia")?.value || null;
    request.fechaSegundaAudiencia = this.form.get("FechaSegundaAudiencia")?.value || null;
    request.fechaCedulaPresentacionSegundaAudiencia = this.form.get("FechaCedulaPresentacionSegundaAudiencia")?.value || null;
    request.fechaCedulaNotificacionSegundaAudiencia = this.form.get("FechaCedulaNotificacionSegundaAudiencia")?.value || null;
    request.idExpediente = this.form.get("IdExpediente")?.value || null;
  }

  private mapFormToUpdateRequest(request: UpdatePruebaTestimonialRequest) {
    request.descripcion = this.form.get("Descripcion")?.value || '';
    request.observaciones = this.form.get("Observaciones")?.value || '';
    request.idUsuarioResponsable = this.form.get("IdUsuarioResponsable")?.value || null;
    request.idPersonaTestigo = this.personaTestigoSeleccionada?.id || null;
    request.idTipoInterviniente = this.form.get("IdTipoInterviniente")?.value || null;
    request.idEstadoPrueba = this.form.get("IdEstadoPrueba")?.value || null;
    request.fechaDeclaraion = this.form.get("FechaDeclaraion")?.value || null;
    request.autoridadDeclaracion = this.form.get("AutoridadDeclaracion")?.value || '';
    request.declaracion = this.form.get("Declaracion")?.value || '';
    request.medioRegistro = this.medioRegistroControl.value || '';
    request.grupo = this.form.get("Grupo")?.value || null;
    request.fechaPrimeraAudiencia = this.form.get("FechaPrimeraAudiencia")?.value || null;
    request.fechaCedulaPresentacionPrimeraAudiencia = this.form.get("FechaCedulaPresentacionPrimeraAudiencia")?.value || null;
    request.fechaCedulaNotificacionPrimeraAudiencia = this.form.get("FechaCedulaNotificacionPrimeraAudiencia")?.value || null;
    request.fechaSegundaAudiencia = this.form.get("FechaSegundaAudiencia")?.value || null;
    request.fechaCedulaPresentacionSegundaAudiencia = this.form.get("FechaCedulaPresentacionSegundaAudiencia")?.value || null;
    request.fechaCedulaNotificacionSegundaAudiencia = this.form.get("FechaCedulaNotificacionSegundaAudiencia")?.value || null;
    request.idExpediente = this.form.get("IdExpediente")?.value || null;
  }

    onExpedienteSelected(expediente: any): void {
    this.expedienteSeleccionado = expediente;
    if (expediente?.id) {
      this.form.get('IdExpediente')?.setValue(expediente.id);
    }
    this.cdr.markForCheck();
  }

  closeDialog() {
    this.dialogRef.close({ event: AccionesDialog.Cancelar });
  }

  create(createRequest: CreatePruebaTestimonialRequest) {
    this.spinner.show("spCrearPruebaTestimonial");
    this.pruebaTestimonialService.create(createRequest)
      .subscribe({
        next: (data) => {
          this.spinner.hide("spCrearPruebaTestimonial");
          this.snackBar.showSuccess("Prueba Testimonial creada correctamente.", "Éxito");
          this.dialogRef.close({ event: AccionesDialog.Agregar, data });
        },
        error: (error) => {
          this.spinner.hide("spCrearPruebaTestimonial");
          this.snackBar.showError(error, "Error");
        }
      });
  }

  update(updateRequest: UpdatePruebaTestimonialRequest) {
    this.spinner.show("spModificarPruebaTestimonial");
    this.pruebaTestimonialService.update(updateRequest)
      .subscribe({
        next: (data) => {
          this.spinner.hide("spModificarPruebaTestimonial");
          this.snackBar.showSuccess("Prueba Testimonial modificada correctamente.", "Éxito");
          this.dialogRef.close({ event: AccionesDialog.Modificar, data });
        },
        error: (error) => {
          this.spinner.hide("spModificarPruebaTestimonial");
          this.snackBar.showError(error, "Error");
        }
      });
  }

  delete(id: number) {
    this.spinner.show("spEliminarPruebaTestimonial");
    this.pruebaTestimonialService.delete(id)
      .subscribe({
        next: () => {
          this.spinner.hide("spEliminarPruebaTestimonial");
          this.snackBar.showSuccess("Prueba Testimonial eliminada correctamente.", "Éxito");
          this.dialogRef.close({ event: AccionesDialog.Eliminar });
        },
        error: (error) => {
          this.spinner.hide("spEliminarPruebaTestimonial");
          this.snackBar.showError(error, "Error");
        }
      });
  }

  private markFormGroupTouched() {
    Object.keys(this.form.controls).forEach(key => {
      this.form.get(key)?.markAsTouched();
    });
    this.cdr.markForCheck();
  }

  trackByItems(index: number, item: any): any {
    return item?.id || index;
  }
}
